import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Row } from "react-bootstrap";
import { formatDateInEN, isNullOrEmpty } from "../../../../../commons/util/HallAppUtil";
import { useMemo, useState } from "react";

import AppContainer from "../../../../../commons/containers/AppContainer";
import AppInput from "../../../../../commons/inputs/AppInput";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import CalendarInput from "../../../../../commons/inputs/CalendarInput";
import HallAppToast from "../../../../../commons/HallAppToast";
import { useMask } from "@react-input/mask";

function ARStep1({ nextStep }) {
  const [startDate, setStartDate] = useState(new Date());
  const [serviceDate, setServiceDate] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [weeks, setWeeks] = useState([]);

  const initialValues = useMemo(() => ({ hours: "" }), []);

  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

  const schema = yup.object().shape({
    hours: yup.string().required("Horário é obrigatório").matches(timeRegex, "Horário inválido"),
  });

  const { Formik } = formik;

  const hoursMask = useMask({ mask: "__:__", replacement: { _: /\d/ } });

  function handleChangeDate(date) {
    setServiceDate(date);
  }

  const weekOptions = useMemo(
    () => [
      { key: "SUNDAY", label: "Dom" },
      { key: "MONDAY", label: "Seg" },
      { key: "TUESDAY", label: "Ter" },
      { key: "WEDNESDAY", label: "Qua" },
      { key: "THURSDAY", label: "Qui" },
      { key: "FRIDAY", label: "Sex" },
      { key: "SATURDAY", label: "Sáb" },
    ],
    []
  );

  const frequencyOptions = useMemo(
    () => [
      { key: "DAILY", label: "Diária", hint: "Todo dia" },
      { key: "WEEKLY", label: "Semanal", hint: "Dias da semana" },
      { key: "FORTNIGHTLY", label: "Quinzenal", hint: "A cada 15 dias" },
      { key: "MONTHLY", label: "Mensal", hint: "Todo mês" },
    ],
    []
  );

  // ✅ Semanal: multi / Quinzenal: single opcional
  function handleWeeks(value) {
    // SEMANAL => multi
    if (frequency === "WEEKLY") {
      setWeeks((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]));
      return;
    }

    // QUINZENAL => single (pode desmarcar clicando novamente)
    if (frequency === "FORTNIGHTLY") {
      setWeeks((prev) => (prev[0] === value ? [] : [value]));
      return;
    }

    // outros => não usa weeks
    setWeeks([]);
  }

  const toNext = (values) => {
    if (!values?.hours) {
      HallAppToast.error("Preencha os campos obrigatórios.");
      return;
    }

    if (isNullOrEmpty(frequency)) {
      HallAppToast.error("Selecione a frequência.");
      return;
    }

    // ✅ Semanal exige pelo menos 1 dia
    if (frequency === "WEEKLY" && weeks.length === 0) {
      HallAppToast.error("Selecione ao menos um dia da semana.");
      return;
    }

    // ✅ Quinzenal NÃO exige dia (se vazio => a cada 15 dias a partir da data inicial)

    const payload = {
      ...values,
      frequency,
      startDate: serviceDate || formatDateInEN(new Date()),
      // ✅ manda weeks apenas quando fizer sentido
      weeks: frequency === "WEEKLY" || frequency === "FORTNIGHTLY" ? weeks : [],
    };

    nextStep(payload);
  };

  const showWeeksWeekly = frequency === "WEEKLY";
  const showWeeksFortnightly = frequency === "FORTNIGHTLY";

  return (
    <div>
      <Formik validationSchema={schema} initialValues={initialValues} enableReinitialize>
        {({ handleChange, values, touched, errors }) => (
          <Form>
            {/* Cabeçalho */}
            <div className="mb-3">
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: "20px" }}>Configurar recorrência</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Defina a data/hora inicial e a frequência.
              </div>
            </div>

            {/* Seção: Data e Hora */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 16,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <div className="mb-2" style={{ fontSize: 14, fontWeight: 700 }}>
                Quando começa?
              </div>
              <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                Escolha a data de início e informe o horário.
              </div>

              <div className="commando-form-service-date" style={{ marginBottom: 14 }}>
                <CalendarInput
                  selectionMode="single"
                  setDates={setStartDate}
                  changeDates={handleChangeDate}
                  values={startDate}
                />
              </div>

              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <AppInput
                    errors={errors}
                    touched={touched}
                    name="hours"
                    onChange={handleChange}
                    value={values.hours}
                    mask={hoursMask}
                    type="text"
                    label="Horário"
                    isRequired
                    placeholder="Ex: 09:30"
                  />
                </Col>
              </Row>
            </div>

            {/* Seção: Frequência */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 16,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <div className="mb-2" style={{ fontSize: 14, fontWeight: 700 }}>
                Com que frequência?
              </div>
              <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                Semanal permite vários dias. Quinzenal permite escolher 1 dia (opcional).
              </div>

              <Row className="g-2">
                {frequencyOptions.map((opt) => {
                  const active = frequency === opt.key;
                  return (
                    <Col xs={6} md={3} key={opt.key}>
                      <button
                        type="button"
                        onClick={() => {
                          setFrequency(opt.key);

                          // ✅ limpa weeks conforme troca
                          if (opt.key === "WEEKLY") {
                            // mantém como está (multi)
                            return;
                          }
                          if (opt.key === "FORTNIGHTLY") {
                            // se estava semanal com vários, reduz pra 1 (opcional manter o primeiro)
                            setWeeks((prev) => (prev?.length > 0 ? [prev[0]] : []));
                            return;
                          }
                          // outros: não usa weeks
                          setWeeks([]);
                        }}
                        className="w-100"
                        style={{
                          borderRadius: 14,
                          border: active ? "2px solid #2f2a7a" : "1px solid #e8e8f0",
                          background: active ? "#f1f3ff" : "#fff",
                          padding: "12px 10px",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#1d1b4a" }}>{opt.label}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {opt.hint}
                        </div>
                      </button>
                    </Col>
                  );
                })}
              </Row>

              {/* Dias da semana (SEMANAL) */}
              {showWeeksWeekly && (
                <>
                  <hr className="my-3" />
                  <div className="mb-2" style={{ fontSize: 13, fontWeight: 700 }}>
                    Dias da semana <span style={{ color: "red" }}>*</span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {weekOptions.map((w) => {
                      const selected = weeks.includes(w.key);
                      return (
                        <button
                          key={w.key}
                          type="button"
                          onClick={() => handleWeeks(w.key)}
                          style={{
                            borderRadius: 999,
                            border: selected ? "1px solid #2f2a7a" : "1px solid #e8e8f0",
                            background: selected ? "#2f2a7a" : "#fff",
                            color: selected ? "#fff" : "#1d1b4a",
                            padding: "8px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {w.label}
                        </button>
                      );
                    })}
                  </div>
                  {weeks.length === 0 && (
                    <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                      Selecione pelo menos um dia.
                    </div>
                  )}
                </>
              )}

              {/* Dias da semana (QUINZENAL - 1 dia opcional) */}
              {showWeeksFortnightly && (
                <>
                  <hr className="my-3" />
                  <div className="mb-2" style={{ fontSize: 13, fontWeight: 700 }}>
                    Dia preferencial (opcional)
                  </div>
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                    Se selecionar um dia, o agendamento será sempre nesse dia da semana.
                    Se não selecionar, será a cada 15 dias a partir da data inicial.
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {weekOptions.map((w) => {
                      const selected = weeks[0] === w.key;
                      return (
                        <button
                          key={w.key}
                          type="button"
                          onClick={() => handleWeeks(w.key)}
                          style={{
                            borderRadius: 999,
                            border: selected ? "1px solid #2f2a7a" : "1px solid #e8e8f0",
                            background: selected ? "#2f2a7a" : "#fff",
                            color: selected ? "#fff" : "#1d1b4a",
                            padding: "8px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {w.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* hint visual se nada selecionado */}
                  {weeks.length === 0 && (
                    <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                      Sem dia selecionado: será criado a cada 15 dias a partir da data de início.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer actions */}
            <div
              className="ar-footer-actions"
              style={{
                position: "sticky",
                bottom: 0,
                padding: "10px 0",
                background: "linear-gradient(to top, #fff 70%, rgba(255,255,255,0))",
              }}
            >
              <AppContainer>
                <AppLinkButton name="Próximo" fullWidth={true} onClick={() => toNext(values)} />
              </AppContainer>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ARStep1;
